import { RefObject } from 'react';

// This type is required because the return type of `computedStyleMap().get()` is inaccurate.
type CSSStyleMapValueReturnType = { value: number; unit: 'number' | 'px' } | undefined;

/**
 * Since element.computedStyleMap() doesn't work in Firefox yet, we have to do some wacky stuff to make
 * window.getComputedStyle work. This will take the return value of window.getComputedStyle().getPropertyValue()
 * and try to return a {@link CSSStyleMapValueReturnType} that makes sense.
 */
function computedStyleToNumber(valueAsString: string): CSSStyleMapValueReturnType {
  const parsedValue = parseFloat(valueAsString);

  if (isNaN(parsedValue)) {
    return undefined;
  }

  if (valueAsString.endsWith('px')) {
    return { value: parsedValue, unit: 'px' };
  }

  if (!isNaN(parseFloat(valueAsString))) {
    return { value: parsedValue, unit: 'number' };
  }

  return undefined;
}

/**
 * Pulls the line height of the supplied ref element via `computedStyleMap()`.
 *
 * If the ref's `current` is `undefined | null` or we can't determine the line height for some reason, this will return `0`
 */
export function getLineHeightInPixels(ref: RefObject<HTMLTextAreaElement | null>) {
  if (!ref.current || !window) {
    return 0;
  }

  const styleMap = window.getComputedStyle(ref.current);

  const lineHeight = computedStyleToNumber(styleMap.getPropertyValue('line-height'));

  if (!lineHeight) {
    return 0;
  }

  // If line-height is a number, we can get the PX height by multiplying it with the font-size.
  if (lineHeight.unit === 'number') {
    const fontSize = computedStyleToNumber(styleMap.getPropertyValue('font-size'));
    return fontSize ? fontSize.value * lineHeight.value : 0;
  }

  if (lineHeight.unit === 'px') {
    return lineHeight.value;
  }

  return 0;
}

/**
 * Calculates the combined height of an element's padding and border styling.
 *
 * If the supplied ref's `current` is `undefined | null` or we can't get the requisite padding and border measurements
 * for some reason, this will return `0`.
 */
export function getExtraHeightFromPaddingAndBorder(ref: RefObject<HTMLDivElement | null>) {
  if (!ref.current || !window) {
    return 0;
  }

  const styleMap = window.getComputedStyle(ref.current);

  const paddingBottom = computedStyleToNumber(styleMap.getPropertyValue('padding-bottom'));
  const paddingTop = computedStyleToNumber(styleMap.getPropertyValue('padding-top'));
  const borderTop = computedStyleToNumber(styleMap.getPropertyValue('border-top-width'));
  const borderBottom = computedStyleToNumber(styleMap.getPropertyValue('border-bottom-width'));

  if (!paddingBottom || !paddingTop || !borderTop || !borderBottom) {
    return 0;
  }

  return paddingBottom.value + paddingTop.value + borderTop.value + borderBottom.value;
}
